import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowsClockwise, Warning, TrendUp, TrendDown } from "@phosphor-icons/react";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tool: preview + apply "Rebaseline Premier ELOs" (1500 + current tournament deltas).
 * Pure UI over /api/admin/rebaseline-premier-elo endpoints (dry_run + rollback).
 */
export default function RebaselinePremierEloCard() {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPreview = async () => {
    setLoading(true); setPreview(null);
    try {
      const res = await axios.post(`${API}/admin/rebaseline-premier-elo`,
        { dry_run: true }, { withCredentials: true });
      setPreview(res.data);
      if (!res.data.preview?.length) {
        toast.info(res.data.message || "No in-progress tournament — nothing to rebaseline.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const applyNow = async () => {
    const pw = await promptSuperPassword({
      title: "Apply rebaseline?",
      description: "Reassigns Premier ELOs to 1500 + current tournament delta. Snapshot is saved for rollback.",
      actionLabel: "Apply Rebaseline",
    });
    if (!pw) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/rebaseline-premier-elo`,
        { dry_run: false }, { headers: { "X-Super-Password": pw }, withCredentials: true });
      setBatchId(res.data.snapshot_batch_id);
      toast.success(`Rebaselined ${res.data.changed} player(s). Snapshot saved.`);
      setConfirmOpen(false);
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  const rollback = async () => {
    if (!window.confirm("Roll back the last rebaseline?")) return;
    const pw = await promptSuperPassword({
      title: "Roll back rebaseline?",
      description: "Restores all Premier players to the ELO captured before the most recent rebaseline.",
      actionLabel: "Rollback",
    });
    if (!pw) return;
    try {
      const res = await axios.post(`${API}/admin/rebaseline-premier-elo/rollback`,
        {}, { headers: { "X-Super-Password": pw }, withCredentials: true });
      toast.success(res.data.message);
      setPreview(null); setBatchId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollback failed");
    }
  };

  const rows = preview?.preview || [];

  return (
    <Card className="bg-card border-border" data-testid="rebaseline-premier-elo-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ArrowsClockwise className="w-4 h-4 text-primary" weight="fill" />
          Rebaseline Premier ELO (1500 + current tournament delta)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-start gap-2 p-2 text-xs rounded bg-muted/20 border border-border">
          <Warning className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
          <div>
            Resets every Premier-tier player's ELO to <strong>1500</strong>, then adds the
            sum of ELO deltas earned in matches from <strong>in-progress tournaments</strong> only.
            Completed tournaments' ELO impact is cleared. A snapshot is saved so rollback is one click.
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={loadPreview} disabled={loading}
            data-testid="preview-rebaseline">
            {loading ? "Loading..." : "Preview changes"}
          </Button>
          {batchId && (
            <Button size="sm" variant="outline" onClick={rollback}
              className="border-destructive/40 text-destructive" data-testid="rollback-rebaseline">
              Roll back last batch
            </Button>
          )}
        </div>

        {preview && rows.length > 0 && (
          <>
            <div className="text-xs text-muted-foreground">
              Tournament(s): {preview.tournaments?.map(t => t.name).join(", ") || "—"}
              {" · "}Matches counted: <strong>{preview.matches_counted ?? 0}</strong>
            </div>
            <div className="border border-border rounded overflow-hidden">
              <table className="w-full text-xs" data-testid="rebaseline-preview-table">
                <thead className="bg-muted/30 text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">Player</th>
                    <th className="text-right p-2">Current</th>
                    <th className="text-right p-2">Tournament Δ</th>
                    <th className="text-right p-2">New ELO</th>
                    <th className="text-right p-2">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.player_id} className="border-t border-border">
                      <td className="p-2">{r.player_name}</td>
                      <td className="p-2 text-right">{r.current_elo}</td>
                      <td className={`p-2 text-right font-medium ${
                        r.delta_from_tournament > 0 ? "text-chart-3" :
                        r.delta_from_tournament < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                        {r.delta_from_tournament > 0 ? "+" : ""}{r.delta_from_tournament}
                      </td>
                      <td className="p-2 text-right font-heading font-bold">{r.new_elo}</td>
                      <td className={`p-2 text-right ${r.change > 0 ? "text-chart-3" : r.change < 0 ? "text-destructive" : ""}`}>
                        {r.change > 0 ? <TrendUp className="inline w-3 h-3 mr-0.5" weight="fill" /> :
                          r.change < 0 ? <TrendDown className="inline w-3 h-3 mr-0.5" weight="fill" /> : null}
                        {r.change > 0 ? "+" : ""}{r.change}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button
              onClick={() => setConfirmOpen(true)}
              className="w-full"
              data-testid="apply-rebaseline"
              disabled={applying}
            >
              Apply rebaseline to {rows.length} premier players
            </Button>
          </>
        )}

        {preview && rows.length === 0 && (
          <p className="text-xs text-muted-foreground">{preview.message}</p>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-rebaseline-dialog">
          <DialogHeader>
            <DialogTitle>Apply Premier ELO rebaseline?</DialogTitle>
            <DialogDescription>
              This will update ELO for {rows.length} premier player(s). A snapshot of their
              current ELOs is saved first — you can undo this from the same card.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={applyNow} disabled={applying} data-testid="confirm-apply-rebaseline">
              {applying ? "Applying…" : "Yes, apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
